// frontend/src/components/boarding/BoardingPassModal.js
import { useState, useEffect } from 'react';
import { X, Download, Printer, QrCode } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import boardingService from '../../services/boardingService';

/**
 * BoardingPassModal
 *
 * Displays boarding pass QR code for riders
 * Features:
 * - Generates and displays QR code
 * - Shows booking details (pre-formatted from parent)
 * - Download as image
 * - Print functionality
 * - Consistent with BookingListPanel formatting
 *
 * Note: Receives pre-formatted data from BookingListPanel to avoid
 * redundant translation/formatting and ensure consistency
 */
const BoardingPassModal = ({
  open,
  onClose,
  booking,
  entityId
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boardingPassData, setBoardingPassData] = useState(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState(null);

  // Load boarding pass when modal opens
  useEffect(() => {
    if (open && booking && entityId) {
      loadBoardingPass();
    }
  }, [open, booking, entityId]);

  /**
   * Load boarding pass from API
   */
  const loadBoardingPass = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get boarding pass from backend
      const response = await boardingService.getBoardingPass(entityId, booking.id, booking.tripId || booking.trip_id || null);

      if (response.success) {
        setBoardingPassData(response.data);

        // Generate QR code image
        const dataURL = await boardingService.generateQRCodeDataURL(response.data.qrData);
        setQrCodeDataURL(dataURL);
      } else {
        setError(response.error || t('errorGeneratingBoardingPass'));
      }
    } catch (err) {
      console.error('Error loading boarding pass:', err);

      // Handle specific error cases
      if (err.response?.data?.code === 'BOARDING_TRACKING_DISABLED') {
        setError(t('boardingTrackingDisabled'));
      } else if (err.response?.data?.error === 'onlyConfirmedBookings') {
        setError(t('onlyConfirmedBookings'));
      } else {
        setError(err.response?.data?.error || t('errorGeneratingBoardingPass'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Download boarding pass as PNG
   */
  const handleDownload = () => {
    if (qrCodeDataURL && booking) {
      boardingService.downloadBoardingPass(qrCodeDataURL, booking.id);
    }
  };

  /**
   * Print boarding pass
   */
  const handlePrint = () => {
    if (qrCodeDataURL && boardingPassData) {
      boardingService.printBoardingPass(qrCodeDataURL, {
        bookingId: booking.id,
        riderName: boardingPassData.booking.riderName,
        riders: boardingPassData.booking.riders,
        luggages: boardingPassData.booking.luggages ?? booking.luggages ?? 0,
        tripDate: boardingPassData.booking.tripDate,
        pickup: boardingPassData.booking.pickup,
        dropoff: boardingPassData.booking.dropoff
      });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={cn("max-w-md w-full rounded-lg shadow-xl", background.secondary)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-blue-500" />
            <h2 className={cn("text-xl font-semibold", text.primary)}>{t('boardingPass')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
              {error === t('boardingTrackingDisabled') && (
                <p className="text-red-600 dark:text-red-300 text-xs mt-2">
                  {t('contactAdminToEnable')}
                </p>
              )}
            </div>
          )}

          {!loading && !error && qrCodeDataURL && boardingPassData && (
            <div>
              {/* QR Code Display */}
              <div className="flex justify-center items-center flex-col py-6 bg-gray-50 dark:bg-gray-800 rounded-lg mb-6">
                <img
                  src={qrCodeDataURL}
                  alt="Boarding Pass QR Code"
                  className="max-w-[280px] h-auto border-8 border-white rounded-lg shadow-md"
                />
                <p className={cn("text-xs text-center mt-4 px-4", text.secondary)}>
                  {t('scanQRCode')}
                </p>
              </div>

              {/* Booking Details */}
              <div className="mb-4">
                <p className={cn("text-sm font-medium mb-3", text.secondary)}>
                  {t('bookingDetails')}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={cn("text-xs", text.secondary)}>{t('bookingId')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>#{booking.id}</p>
                  </div>

                  {boardingPassData.booking.riderName && (
                    <div>
                      <p className={cn("text-xs", text.secondary)}>{t('riderName')}</p>
                      <p className={cn("text-sm font-medium", text.primary)}>
                        {boardingPassData.booking.riderName}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className={cn("text-xs", text.secondary)}>{t('riders')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {boardingPassData.booking.riders}
                    </p>
                  </div>

                  <div>
                    <p className={cn("text-xs", text.secondary)}>{t('luggage')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {boardingPassData.booking.luggages ?? booking.luggages ?? 0}
                    </p>
                  </div>

                  <div>
                    <p className={cn("text-xs", text.secondary)}>{t('date')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {booking.formattedDate}
                    </p>
                  </div>

                  <div>
                    <p className={cn("text-xs", text.secondary)}>{t('time')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {booking.formattedTime}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className={cn("text-xs", text.secondary)}>{t('pickupLocation')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {booking.pickupLocationName}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className={cn("text-xs", text.secondary)}>{t('dropoffLocation')}</p>
                    <p className={cn("text-sm font-medium", text.primary)}>
                      {booking.dropoffLocationName}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {t('close')}
          </button>
          {qrCodeDataURL && (
            <>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                {t('download')}
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                <Printer className="h-4 w-4" />
                {t('print')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardingPassModal;
